import {EChannelType, EContentType, TChannelType, TContentType, TItemId} from 'src/constants';
import type {
  TButton,
  TCatalog,
  TListPickerItem,
  TOrderProduct,
  TReferral,
} from 'src/containers/Report/definitions';

import {
  EChannelParameter,
  ESystemParameter,
  TItem as TParametersItem,
} from '../Parameters/definitions';
import {EAddress, TAddressItem} from '../Subscribers/definitions';

enum EDialogStatus {
  ANSWERED = 'ANSWERED',
  UNANSWERED = 'UNANSWERED',
}

type TDialogStatus = keyof typeof EDialogStatus;

enum EMessageDirection {
  IN = 'IN',
  OUT = 'OUT',
}

type TMessageDirection = keyof typeof EMessageDirection;

enum EDeliveryStatus {
  ENQUEUED = 'ENQUEUED',
  SENT = 'SENT',
  FAILED = 'FAILED',
  DELIVERED = 'DELIVERED',
  CANCELLED = 'CANCELLED',
  UNDELIVERED = 'UNDELIVERED',
  READ = 'READ',
  EXPIRED = 'EXPIRED',
  SUSPENDED = 'SUSPENDED',
  BLOCKED = 'BLOCKED',
  ACCEPTED = 'ACCEPTED',
  INVALID = 'INVALID',
}

type TDeliveryStatus = keyof typeof EDeliveryStatus;

type TAttachment = {
  url?: string;
  name?: string;
  size?: number;
};

type TMessageContent = {
  type: TContentType;
  attachment?: TAttachment;
  location: {
    longitude?: number;
    latitude?: number;
    address?: string;
  };
  contact?: {
    name: {
      name: string;
      firstName?: string;
      lastName?: string;
    };
    phones: {
      phone: string;
      type: string;
      userId: string;
    }[];
  };
  action?: string;
  payload?: string;
  caption?: string;
  shouldCollectClicks?: boolean;
  requestCallPermission?: boolean;
  header?: {
    text?: string;
    imageUrl?: string;
    documentUrl?: string;
    documentName?: string;
    videoUrl?: string;
    videoName?: string;
  };
  text?: string;
  footer?: {text: string};
  buttons?: TButton[];
  flowId?: string;
  language?: string;
  securityRecommendation?: boolean;
  codeExpirationMinutes?: number;
  referral?: TReferral;
  items?: TListPickerItem[];
  order?: TOrderProduct;
  callPermissionReply?: {
    response: 'accept' | 'reject';
    expirationTimestamp?: string;
  };
  product?: {
    id: string;
    catalogId: string;
  };
  catalog?: TCatalog;
  listPicker?: {
    sections?: {
      items?: TListPickerItem[];
    }[];
  };
};

type TDialogMessage = {
  id: TItemId;
  channelType: TChannelType;
  direction: TMessageDirection;
  messageContent: TMessageContent;
  deliveryStatus?: TDeliveryStatus;
  deliveryStatusAt: string;
  sentOrReceivedAt: string;
  messageSource: EMessageSource;
  initiatorUserId: number | null;
};

type TDialog = {
  id: TItemId;
  tenantId: TItemId;
  subjectId: TItemId;
  subscriberAddress: string;
  subscriberAddressType: string;
  channelType: TChannelType;
  lastMessageId: TItemId;
  lastMessageAt: string;
  lastMessageDirection: TMessageDirection;
  lastMessageText: string;
  lastMessageContentType: EContentType;
  status: TDialogStatus;
  statusUpdatedAt: string;
  createdAt: string;
  updatedAt: string;
  messages: TDialogMessage[];
};

type TDialogMessageContent = {
  type: string;
  text?: string;
  url?: string;
  caption?: string;
  name?: string;
};

type TSendMessagePayload = {
  dialogId: TItemId;
  content: {
    whatsAppContent?: TDialogMessageContent;
    maxBotContent?: TDialogMessageContent;
    maxContent?: TDialogMessageContent;
    telegramContent?: TDialogMessageContent;
  };
};

type TSendTemplateContent = {
  channelType: TChannelType;
  contentType: EContentType;
  contentHolder: {
    text: string;
    header?: {
      headerType?: EContentType;
      text?: string;
      attachment?: TFile | null;
    };
    footer?: {
      text: string;
    };
    buttons?: unknown[];
  };
  messageMatcherId?: TItemId | null;
};

type TSendTemplateMessagePayload = {
  dialogId: TItemId;
  content: TSendTemplateContent;
};

type TSendTemplateMessageResponse = {
  dialogId: TItemId;
  outMessageId: TItemId;
};

type TStartDialogPayload = {
  subjectId: TItemId;
  subscriberAddress: string;
  subscriberAddressType: string;
  channelType: TChannelType;
  content: {
    whatsAppContent?: TDialogMessageContent;
    maxBotContent?: TDialogMessageContent;
    maxContent?: TDialogMessageContent;
    telegramContent?: TDialogMessageContent;
  };
};

type TStartTemplateDialogPayload = {
  subjectId: TItemId;
  subscriberAddress: string;
  subscriberAddressType: string;
  content: TSendTemplateContent;
};

type TPollPayload = {
  timeoutSeconds: number;
  afterUpdatedAt: string;
};

type TPollResponse = {
  timeout: boolean;
  latestUpdatedAt: string;
};

type TSendMessageResponse = {
  outMessageId: TItemId;
};

type TStartDialogResponse = {
  dialogId: TItemId;
  outMessageId: TItemId;
};

type TGetDialogMessagesPayload = {
  dialogId: TItemId;
};

type TGetDialogsPayload = {
  offset: number;
  limit: number;
  subscriberAddresses?: string[];
  status?: EDialogStatus;
};

const DIALOGS_POLLING_INTERVAL = 5000;
const WHATSAPP_DIALOG_EXPIRED_MS = 24 * 60 * 60 * 1000;
const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_DELAY = 300;

const INITIATABLE_CHANNEL_TYPES: EChannelType[] = [EChannelType.WHATSAPP, EChannelType.MAX];

enum ESearchResultState {
  IDLE = 'IDLE',
  LOADING = 'LOADING',
  DIALOGS_FOUND = 'DIALOGS_FOUND',
  SUBSCRIBER_FOUND_NO_DIALOGS = 'SUBSCRIBER_FOUND_NO_DIALOGS',
  NOT_FOUND_CAN_CREATE = 'NOT_FOUND_CAN_CREATE',
  NOT_FOUND_NO_INITIATION = 'NOT_FOUND_NO_INITIATION',
}

const ALLOWED_DOCUMENT_EXTENSIONS = [
  '.doc',
  '.docx',
  '.txt',
  '.rtf',
  '.dot',
  '.dotx',
  '.odt',
  '.odf',
  '.fodt',
  '.info',
  '.pdf',
  '.xls',
  '.xlsx',
  '.xps',
  '.pdax',
  '.eps',
  '.csv',
  '.png',
  '.jpeg',
  '.jpg',
  '.gif',
  '.pptx',
  '.mp4',
  '.3gpp',
  '.amr',
  '.mpeg',
  '.ogg',
  '.aac',
  '.zip',
];

const NAME_PARAMETER_CODES = [
  ESystemParameter.FIRST_NAME,
  ESystemParameter.LAST_NAME,
  EChannelParameter.WHATSAPP_DISPLAY_NAME,
  EChannelParameter.WHATSAPP_USERNAME,
  EChannelParameter.MAX_FIRST_NAME,
  EChannelParameter.MAX_LAST_NAME,
];

type TDialogSubscriberResponse = {
  address: string;
  type: EAddress;
  subscriberId: TItemId;
  addresses: TAddressItem[];
  parameterValues: TParametersItem[];
};

type TDialogSubscriber = {
  id: TItemId;
  name: string;
  addresses: Record<string, TAddressItem>;
  parameters: Record<string, TParametersItem>;
};

enum EMessageSource {
  JSON_API = 'jsonapi',
  JIVO = 'jivo',
  IMHPX = 'imhpx',
  BROADCAST = 'broadcast',
  BROADCAST_TEST_MESSAGE = 'broadcast-test-message',
  INTERNAL_API = 'internalapi',
  FLOW = 'flow',
  DIALOG = 'dialog',
  OUT_MESSAGE = 'out-message',
  WHATSAPP_BUSINESS_APP = 'wa_business_app',
}

export {
  EDialogStatus,
  EMessageDirection,
  EDeliveryStatus,
  ESearchResultState,
  TDialog,
  TDialogMessage,
  TMessageContent,
  TAttachment,
  TDialogStatus,
  TMessageDirection,
  TDeliveryStatus,
  TSendMessagePayload,
  TSendTemplateContent,
  TSendTemplateMessagePayload,
  TSendTemplateMessageResponse,
  TStartDialogPayload,
  TStartTemplateDialogPayload,
  TPollPayload,
  TPollResponse,
  TSendMessageResponse,
  TStartDialogResponse,
  TGetDialogMessagesPayload,
  TGetDialogsPayload,
  TDialogSubscriber,
  TDialogSubscriberResponse,
  EMessageSource,
  ALLOWED_DOCUMENT_EXTENSIONS,
  DIALOGS_POLLING_INTERVAL,
  WHATSAPP_DIALOG_EXPIRED_MS,
  MIN_SEARCH_LENGTH,
  SEARCH_DEBOUNCE_DELAY,
  INITIATABLE_CHANNEL_TYPES,
  NAME_PARAMETER_CODES,
};
